import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { SelectMenu, useToast, useConfirm } from '../ui';
import { settingsAPI } from '../../services/api';
import {
  ArrowUpTrayIcon,
  TrashIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../ui/PageHeader';
import ListShell from '../ui/ListShell';

/**
 * FilesPage — Settings › Files (renamed from Documents).
 *
 * Iter18 Polish Round 2 §2.16:
 *   • Rename from "Documents" to "Files".
 *   • Fix upload button (FileReader.readAsDataURL + settingsAPI.addListItem).
 *   • Search + filter in PageHeader actions.
 *   • Render as .data-table (Name · Type · Size · Uploaded · Actions).
 *   • useConfirm for delete.
 *
 * Tech debt (backlog): S3 presigned upload flow — currently stores base64
 * data_url on the settings record. See workspace ledger.
 */

const TYPE_FILTERS = [
  { value: 'all',   label: 'All types' },
  { value: 'pdf',   label: 'PDF' },
  { value: 'image', label: 'Image' },
  { value: 'docx',  label: 'DOCX' },
  { value: 'other', label: 'Other' },
];

function mimeCategory(type) {
  if (!type) return 'other';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || type === 'application/msword') return 'docx';
  return 'other';
}

function categoryLabel(cat) {
  return { pdf: 'PDF', image: 'Image', docx: 'DOCX', other: 'File' }[cat] || 'File';
}

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getList('documents');
      setDocuments(res || []);
    } catch (err) {
      toast.error('Failed to load files', { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const triggerUpload = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // PT-33a / B2 (2026-07-27): size guard. Base64 encoding inflates ~33%,
    // and DDB items cap at 400KB. A 300KB raw file becomes ~400KB base64 —
    // at the limit. Reject anything larger with a clear message rather than
    // silently truncating or hitting a DDB write error downstream.
    const MAX_FILE_SIZE = 300 * 1024; // 300KB
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      toast.error(
        `File${oversized.length > 1 ? 's' : ''} too large: ${oversized.map((f) => f.name).join(', ')}`,
        { description: `Maximum file size is ${Math.round(MAX_FILE_SIZE / 1024)}KB for inline storage. Larger files will be supported when S3 uploads land.` },
      );
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        // Iter18 R2 tech debt: store as inlined data_url for now. S3 presigned
        // uploads are the correct long-term solution — see workspace ledger.
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target?.result || '');
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });
        // eslint-disable-next-line no-await-in-loop
        await settingsAPI.addListItem('documents', {
          name: file.name,
          size: file.size,
          type: file.type,
          data_url: dataUrl,
          uploaded_at: new Date().toISOString(),
        });
      }
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
      await fetchDocuments();
    } catch (err) {
      toast.error('Failed to upload files', { description: err?.message });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (doc) => {
    const ok = await confirm({
      title: 'Delete file?',
      message: `Delete "${doc.name}"? This can't be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await settingsAPI.deleteListItem('documents', doc.id);
      toast.success('File deleted');
      await fetchDocuments();
    } catch (err) {
      toast.error('Failed to delete file', { description: err?.message });
    }
  };

  const handleDownload = (doc) => {
    if (!doc.data_url) {
      toast.error('This file does not have a downloadable URL. Re-upload to make it downloadable.');
      return;
    }
    // Trigger download via a temporary anchor.
    const link = document.createElement('a');
    link.href = doc.data_url;
    link.download = doc.name || 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (typeFilter !== 'all' && mimeCategory(d.type) !== typeFilter) return false;
      if (q && !(d.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [documents, search, typeFilter]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
        <div className="h-32 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Files"
        subtitle="Upload and manage files shared across your account. Search or filter by type."
        actions={
          <>
            {/* Iter18 R2 Round 3 (F): Only the primary Upload CTA lives in
                PageHeader.actions now — Search + Type filter moved to the
                toolbar row below (see the search/filter row further down).
                Upload uses the default (md) size for the required visual
                weight of a primary CTA. */}
            <Button disabled={uploading} onClick={triggerUpload}>
              <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              className="hidden"
              aria-label="Upload files"
            />
          </>
        }
      />

      {/* Iter18 R2 Round 4 (F): Canonical search bar via ListShell, matching
          the pattern used across Customers, Quotes, Jobs list pages. */}
      <ListShell
        search={search}
        onSearchChange={setSearch}
        placeholder="Search files..."
        rightSlot={
          <SelectMenu
            value={typeFilter}
            onChange={setTypeFilter}
            options={TYPE_FILTERS}
            width="auto"
            className="w-40"
          />
        }
      />

      {filtered.length === 0 ? (
        <Card className="text-center py-12">
          <DocumentIcon className="mx-auto h-10 w-10" style={{ color: 'var(--color-text-muted)' }} />
          <p className="mt-2 text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
            {documents.length === 0 ? 'No files uploaded yet' : 'No files match your filters'}
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {documents.length === 0
              ? 'Upload files to share across your account.'
              : 'Try adjusting the search or type filter.'}
          </p>
          {documents.length === 0 && (
            <div className="mt-4">
              <Button size="sm" variant="secondary" onClick={triggerUpload} disabled={uploading}>
                <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                Upload File
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <div
          className="overflow-hidden"
          style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}
        >
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Size</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc, idx) => (
                <tr key={doc.id || idx}>
                  <td>
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                      <span className="font-medium truncate" style={{ color: 'var(--color-text-default)' }}>{doc.name}</span>
                    </div>
                  </td>
                  <td>{categoryLabel(mimeCategory(doc.type))}</td>
                  <td style={{ textAlign: 'right' }}>{formatFileSize(doc.size)}</td>
                  <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        className="p-1.5 rounded hover:bg-[color:var(--neutral-100)]"
                        aria-label={`Download ${doc.name}`}
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(doc)}
                        className="p-1.5 rounded hover:bg-red-50"
                        aria-label={`Delete ${doc.name}`}
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
